import { useEffect, useMemo, useRef, useState } from 'react';
import { readRegistryCollection, writeRegistryCollection } from '../../data/registryStore';
import {
  FORM_REGISTRY_BLUEPRINTS,
  FORM_REGISTRY_STORAGE_KEY,
  STANDARD_OVERRIDES_STORAGE_KEY,
  SUPPLEMENTAL_FORM_STORAGE_KEY,
  cloneFormBlueprint,
  getAddedStandardFields,
  getCustomFormsForScope,
  getStandardFormForScope,
  normalizeFormBlueprint,
} from './formRegistryData';

// Bump this whenever formRegistryData.js's default blueprint list is pruned or
// restructured (ids removed, renamed, or merged into another instrument). On
// mismatch the entire cached collection is wiped back to exactly the current
// code defaults — a full reset rather than an ever-growing blocklist of
// specific retired ids, since there is no reliable way to enumerate every id a
// browser may have cached across the app's history.
const FORM_REGISTRY_CACHE_VERSION = '2026-09-16-ppp-cross-cutting';
const CACHE_VERSION_KEY = 'nlims_form_registry_cache_version';

// Storage keys whose changes mean the merged form list must be re-read.
const WATCHED_KEYS = new Set([SUPPLEMENTAL_FORM_STORAGE_KEY, STANDARD_OVERRIDES_STORAGE_KEY]);
// registryStore prefixes every key with this in localStorage.
const CACHE_PREFIX = 'nlims_cache_';

function ensureCacheVersion() {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (window.localStorage.getItem(CACHE_VERSION_KEY) === FORM_REGISTRY_CACHE_VERSION) return;
    writeRegistryCollection(FORM_REGISTRY_STORAGE_KEY, FORM_REGISTRY_BLUEPRINTS);
    window.localStorage.setItem(CACHE_VERSION_KEY, FORM_REGISTRY_CACHE_VERSION);
  } catch {
    // Best-effort — if localStorage access throws, there is nothing to reset.
  }
}
ensureCacheVersion();

function isCustomForm(form) {
  return String(form?.type ?? '').toLowerCase() === 'custom';
}

// What makes a Standard form "modified": its name, sections and field
// definitions. Preview values typed into the viewer and timestamps are
// ignored so just looking at / previewing a form never creates an override.
function designSignature(form) {
  return JSON.stringify({
    name: form?.name ?? '',
    sections: (form?.sections ?? []).map((section) => ({
      id: section.id,
      title: section.title,
      fields: (section.fields ?? []).map(({ value, otherValue, ...field }) => field),
    })),
  });
}

const DEFAULT_SIGNATURES = new Map(
  FORM_REGISTRY_BLUEPRINTS.map((blueprint) => {
    const normalized = normalizeFormBlueprint(blueprint);
    return [normalized.id, designSignature(normalized)];
  })
);

function readOverrides() {
  const rows = readRegistryCollection(STANDARD_OVERRIDES_STORAGE_KEY);
  return new Map((Array.isArray(rows) ? rows : []).map((row) => [row.id, row]));
}

// Core (Class A) blueprints come from code, with any admin modification from
// Form Registry laid over the top (and flagged `modified`) — so the code
// default is never lost and a code fix still reaches unmodified forms.
// Class B (Supplemental) forms live in their own storage key, logically
// separate from the Core blueprints (per the Dynamic Form Builder
// framework's "Core Data Store and Supplemental Data Store are separate
// logical domains").
// A built-in field's input type is part of the standard definition, not an
// admin customisation — so a saved override always takes the field's current
// type (and exclusive options) from code. E.g. Sex became a checkbox field;
// every saved copy of the Livestock Population form follows automatically.
function withStandardFieldTypes(override, blueprint) {
  const defaults = new Map(
    (blueprint.sections ?? []).flatMap((section) => section.fields ?? []).map((field) => [field.id, field])
  );
  return {
    ...override,
    sections: (override.sections ?? []).map((section) => ({
      ...section,
      fields: (section.fields ?? []).map((field) => {
        const standard = defaults.get(field.id);
        if (!standard || standard.type === field.type) return field;
        return { ...field, type: standard.type, exclusiveOptions: standard.exclusiveOptions };
      }),
    })),
  };
}

function readRegistryForms() {
  const overrides = readOverrides();
  const standard = FORM_REGISTRY_BLUEPRINTS.map((blueprint) => {
    const override = overrides.get(blueprint.id);
    return override
      ? { ...withStandardFieldTypes(override, blueprint), type: 'standard', modified: true }
      : blueprint;
  });
  const supplemental = readRegistryCollection(SUPPLEMENTAL_FORM_STORAGE_KEY) ?? [];
  return [...standard, ...supplemental]
    .map(normalizeFormBlueprint)
    .map(cloneFormBlueprint);
}

function persistForms(forms) {
  const supplemental = forms.filter(isCustomForm).map(cloneFormBlueprint);
  writeRegistryCollection(SUPPLEMENTAL_FORM_STORAGE_KEY, supplemental);

  const overrides = forms
    .filter((form) => !isCustomForm(form))
    .filter((form) => DEFAULT_SIGNATURES.has(form.id) && designSignature(form) !== DEFAULT_SIGNATURES.get(form.id))
    .map(({ modified, ...form }) => cloneFormBlueprint(form));
  const previous = readRegistryCollection(STANDARD_OVERRIDES_STORAGE_KEY) ?? [];
  if (JSON.stringify(previous) !== JSON.stringify(overrides)) {
    writeRegistryCollection(STANDARD_OVERRIDES_STORAGE_KEY, overrides);
  }
}

export function isStandardFormModified(form) {
  if (!form || isCustomForm(form) || !DEFAULT_SIGNATURES.has(form.id)) return false;
  return designSignature(form) !== DEFAULT_SIGNATURES.get(form.id);
}

// Drops the admin's modification to a Standard form so it goes back to the
// code default everywhere (Form Registry and the module's Add Record page).
export function resetStandardForm(formId) {
  const overrides = (readRegistryCollection(STANDARD_OVERRIDES_STORAGE_KEY) ?? [])
    .filter((row) => row.id !== formId);
  writeRegistryCollection(STANDARD_OVERRIDES_STORAGE_KEY, overrides);
}

export function useFormRegistryCollection() {
  const [forms, setForms] = useState(() => readRegistryForms());
  const formsRef = useRef(forms);
  const persistedRef = useRef(JSON.stringify(forms));

  useEffect(() => {
    formsRef.current = forms;
  }, [forms]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const reload = () => {
      const nextForms = readRegistryForms();
      const serialized = JSON.stringify(nextForms);

      if (serialized === JSON.stringify(formsRef.current)) return;

      persistedRef.current = serialized;
      setForms(nextForms);
    };

    // Same tab: registryStore dispatches this on every write.
    const handleRegistryUpdate = (event) => {
      if (WATCHED_KEYS.has(event?.detail?.key)) reload();
    };
    // Other tabs: the browser fires `storage` when localStorage changes.
    const handleStorage = (event) => {
      if (!event.key || WATCHED_KEYS.has(String(event.key).replace(CACHE_PREFIX, ''))) reload();
    };

    window.addEventListener('nlims:registry-updated', handleRegistryUpdate);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('nlims:registry-updated', handleRegistryUpdate);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  useEffect(() => {
    const serialized = JSON.stringify(forms);
    if (serialized === persistedRef.current) return;
    persistedRef.current = serialized;
    persistForms(forms);
  }, [forms]);

  return [forms, setForms];
}

// The Standard form for a module/submodule, including any modification
// saved in Form Registry. Module Add Record pages build their fields from it.
export function useFormRegistrySchema(moduleKey, submoduleKey) {
  const [forms, setForms] = useFormRegistryCollection();

  const schema = useMemo(
    () => getStandardFormForScope(forms, moduleKey, submoduleKey) ?? null,
    [forms, moduleKey, submoduleKey]
  );

  return { forms, setForms, schema };
}

// Everything Form Registry contributes to a module page beyond its built-in
// fields: fields added to its Standard form, plus every Custom form bound to
// the same module/submodule.
export function useModuleRegistryExtras(moduleKey, submoduleKey) {
  const [forms] = useFormRegistryCollection();

  return useMemo(() => {
    const standard = getStandardFormForScope(forms, moduleKey, submoduleKey);
    return {
      standard,
      addedSections: getAddedStandardFields(standard),
      customForms: getCustomFormsForScope(forms, moduleKey, submoduleKey),
    };
  }, [forms, moduleKey, submoduleKey]);
}

export function readRegistryFormSchema(moduleKey, submoduleKey) {
  const forms = readRegistryForms();
  return getStandardFormForScope(forms, moduleKey, submoduleKey) ?? null;
}
